import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { tagHotjarRecording } from '../../helpers/util'

import useRequest from '../../hooks/useRequest'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import { useHistory } from 'react-router-dom'
import AppRoutes from '../../routes/appRoutes'
import CardInfo from '../../components/CardInfo/CardInfo'
import InfoLine from '../../components/CardInfo/components/InfoLine'
import CustomDivider from '../../components/CardInfo/components/CustomDivider'
import CellLocation from '../../components/Table/components/CellLocation'
import CarouselImages from '../../components/CarouselImages/CarouselImages'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import CellLink from '../../components/Table/components/CellLink'
import useScreen from '../../hooks/useScreen'
import { getPendingRequestById } from '../../../../indexedDb/pendingRequests'
import { getRequestDetailsById } from '../../../../indexedDb/requestsDetails'
import { useNetworkStatus } from '../../utils/networkStatus'
import {
    formatStringToDate,
    formatTimestampToDate,
} from '../../utils/formatTimestampToDate'
import { isTimeStamp } from '../../utils/isTimeStamp'
import PendingMessage from '../../components/PendingMessage.js/PendingMessage'
import InformationCard from '../CreateRequestStepper.js/components/InformationCard'
import NeedsReviewWarningCard from '../CreateRequestStepper.js/components/NeedsReviewWarningCard'

const ViewPartsRequestPage = props => {
    const [errorMessage, setErrorMessage] = useState('')
    const [request, setRequest] = useState({})
    const [images, setImages] = useState([])
    const [title, setTitle] = useState('')
    const [isPendingRequest, setIsPendingRequest] = useState(false)
    const { handleGetRequestInfo } = useRequest()
    const { changeLoading } = useGlobalLoading()
    const { isMobile } = useScreen()
    const history = useHistory()
    const { match } = props
    const { isOffline } = useNetworkStatus()

    const onGetRequestInfoSuccess = response => {
        let requestInfo = response?.data?.request
        setRequest(requestInfo)
        setTitle(
            isMobile
                ? requestInfo?.label
                : `Request Details: ${requestInfo?.name}`,
        ) + setImages(response?.data?.images)
        changeLoading(false)
    }

    const onGetRequestInfoError = error => {
        tagHotjarRecording('API Error')

        let message = error.response.data.message
        setErrorMessage(message)
        console.log(errorMessage)
        changeLoading(false)
    }

    // Get data (only if component is still mounted)
    useEffect(() => {
        const fetchPendingRequestInfo = async () => {
            let requestId = match?.params?.requestId
            changeLoading(true)
            let pendingRequestFromIndexDB

            const numericRequestId = Number(requestId)
            if (isTimeStamp(numericRequestId)) {
                pendingRequestFromIndexDB = await getPendingRequestById(
                    numericRequestId,
                )
            }

            if (pendingRequestFromIndexDB) {
                setIsPendingRequest(true)
                setTitle('Pending Request')
                setRequest(pendingRequestFromIndexDB)
            } else if (isOffline) {
                try {
                    const offlineRequest = await getRequestDetailsById(
                        requestId,
                    )
                    if (offlineRequest) {
                        setRequest(offlineRequest)
                    } else {
                        console.warn('Request not found in IndexedDB')
                    }
                } catch (error) {
                    console.error(
                        'Error fetching request from IndexedDB:',
                        error,
                    )
                }
            } else {
                try {
                    await handleGetRequestInfo(
                        requestId,
                        'parts',
                        onGetRequestInfoSuccess,
                        onGetRequestInfoError,
                    )
                } catch (error) {
                    console.error('Error fetching request:', error)
                }
            }
            changeLoading(false)
        }

        fetchPendingRequestInfo()
    }, [isOffline, match?.params?.requestId])

    const renderHeader = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start p-4 bg-white border-b border-grayLines lg:gap-4 gap-2">
                <p
                    role="button"
                    onClick={() => {
                        history?.goBack()
                    }}
                    className="text-textDarkGray text-sm font-medium"
                >
                    {'< Go Back'}
                </p>
                <p className="text-black90 font-semibold text-lg">{title}</p>
            </div>
        )
    }

    const renderRequestInformationContent = () => {
        const requestLabel = !isPendingRequest
            ? request?.name || '-'
            : 'Unavailable'
        const typeOfRequest = !isPendingRequest
            ? request?.typeDisplay || '-'
            : request?.showType
        const requestDate = !isPendingRequest
            ? request?.formattedDate || '-'
            : formatTimestampToDate(request?.id)
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start py-2">
                <InfoLine label="Request Number" value={requestLabel} />
                <CustomDivider />
                <InfoLine label="Type of Request" value={typeOfRequest} />
                <CustomDivider />
                <InfoLine
                    label="Submitted by"
                    value={request?.requestedByName || '-'}
                />
                <CustomDivider />
                <InfoLine label="Submitted on" value={requestDate} isLast />
            </div>
        )
    }

    const renderAssetDetailsInformationContent = () => {
        const requestAssetName =
            request?.customerAlias || request?.equipmentName
        const requestId = request?.equipmentSFId || request?.equipmentId
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start py-2">
                <InfoLine
                    label="Customer Unit #"
                    value={
                        requestAssetName ? (
                            <CellLink
                                key="linkToAsset"
                                text={requestAssetName}
                                to={
                                    requestId
                                        ? AppRoutes.fleetAsset(requestId) +
                                          '?activeTab=overviewTab'
                                        : '/'
                                }
                            />
                        ) : (
                            '-'
                        )
                    }
                />
                <CustomDivider />
                <InfoLine label="Make" value={request?.make || '-'} />
                <CustomDivider />
                <InfoLine label="Model" value={request?.model || '-'} isLast />
                <CustomDivider />
                <InfoLine
                    label="Location"
                    value={
                        (
                            <CellLocation
                                isInCard
                                key="assetLocation"
                                address={request?.address || '-'}
                                label={request?.locationName || '-'}
                            />
                        ) || '-'
                    }
                    isLast
                />
            </div>
        )
    }

    const renderRequestDescriptionContent = () => {
        return (
            <div className="w-full h-auto flex flex-col items-start justify-start py-2">
                <InfoLine
                    label="Description"
                    value={request?.description || '-'}
                />
                <CustomDivider />
                <InfoLine
                    label="Needed By"
                    value={
                        request?.neededBy ||
                        formatStringToDate(request?.neededByDate) ||
                        '-'
                    }
                    isLast
                />
            </div>
        )
    }

    const renderImages = images => {
        let allImages = []
        if (isPendingRequest) {
            allImages = images.map(img =>
                img.data instanceof Blob
                    ? URL.createObjectURL(img.data)
                    : img.data,
            )
        } else {
            allImages = images?.files?.map(a => a?.url)
        }

        return (
            <div className="w-full lg:h-100 h-auto relative">
                <CarouselImages
                    heightImage={isMobile ? 'auto' : 400}
                    images={allImages}
                />
            </div>
        )
    }

    const renderAttachments = () => {
        const hasImages = isPendingRequest
            ? request?.images && request?.images.length > 0
            : images?.files?.length > 0

        const imageList = isPendingRequest ? request?.images : images
        return (
            <div className="w-full h-auto rounded-2xl overflow-hidden max-w-full max-h-full my-2">
                {hasImages ? (
                    renderImages(imageList)
                ) : (
                    <p className="text-sm text-center text-textDarkerGray font-medium">
                        No files found
                    </p>
                )}
            </div>
        )
    }

    const renderContentInfo = () => {
        return (
            <div className="lg:w-220 w-full m-auto h-auto flex flex-col items-start justify-start gap-6 lg:mt-12 mt-0 lg:p-0 p-4">
                {request.status === 'Pending' && (
                    <InformationCard request={request} />
                )}
                {request.status === 'Needs Review' && (
                    <NeedsReviewWarningCard request={request} />
                )}

                <CardInfo
                    title="Request Information"
                    content={renderRequestInformationContent()}
                />

                <CardInfo
                    title="Asset Details"
                    content={renderAssetDetailsInformationContent()}
                />

                <CardInfo
                    title="Request Description"
                    content={renderRequestDescriptionContent()}
                />

                <CardInfo
                    title="Attachments"
                    hasPaddingContent={!isMobile}
                    content={renderAttachments()}
                />
                {isOffline && isPendingRequest && <PendingMessage bottom={0} />}
            </div>
        )
    }

    return (
        <ContentLayout>
            {renderHeader()}
            {renderContentInfo()}
        </ContentLayout>
    )
}

ViewPartsRequestPage.propTypes = {
    location: PropTypes.object,
    match: PropTypes.shape({
        params: PropTypes.shape({
            requestId: PropTypes.string,
        }),
    }),
}

export default ViewPartsRequestPage
